import React, { useCallback, useState } from "react";
import styled from "styled-components";
import { RotateCcw, Save, Search, X } from "react-feather";
import { useQuery, useQueryClient } from "react-query";
import Fuse from "fuse.js";
import {
  Banner,
  Button,
  ChangesTrigger,
  Input,
  InputGroup,
  PageWrapperTwoColumn,
  Section,
  Select,
  SelectOption,
  Spinner,
  Table,
  Tabs,
  TreeRow,
  TreeView,
} from "@cinesys/ui/components";
import AbHeader from "../AbHeader";
import { sortByKey } from "../../shared/sortByKey";
import { AB_VAULT_INTERFACE_ID } from "../config";
import { useInterfaceAccess } from "../../../hooks/useInterfaceAccess";
import InterfaceAccessNotice from "../../../components/InterfaceAccessNotice";
import { DelayedSpinner } from "@cinesys/experience-ui/components";
import {
  EntityItem,
  SelectedEntity,
  VaultRights,
  getCandidates,
  getTeams,
  getOwnAssignments,
  getUserInfo,
  getUsers,
  isAdmin,
  isNotUser,
  saveAssignment,
  getAssignmentForEntity,
} from "./service";

/**
 * Ported almost verbatim from
 * apps/vault-frontend/src/features/candidate-assignments/CandidateAssignmentsScreen.tsx
 * (spec §6.2/§6.3) — all screen logic/state/queries are unchanged. Structural change: vault's
 * `FeatureLayout` (a standalone two-pane page shell with its own AB logo + title rail) is REMOVED —
 * this screen now renders as routed content INSIDE the header-less `CustomerLayout` (see that
 * file's doc comment), so it renders its own product-specific tab row (`AbHeader`, trimmed to
 * just that — `ShellHeader`, mounted above it, owns the rest of the chrome) using the
 * `--ab*` teal palette (`VaultThemeProvider`, wrapped around this route in `AppRouter.tsx`) — NOT
 * the `spotter` tool's slate/amber palette. "Candidate Assignment" is now `AbHeader`'s own tab
 * label (`config.ts`'s `AB_TOP_TABS`), not an in-body `<h2>` — same "page title is a tab, not a
 * heading" pattern as NFL's `FieldsScreen`.
 *
 * `ScreenShell` below no longer owns any branding — it's just the loading-state centering wrapper
 * around this screen's real content, sitting under `AbHeader`. No page-level background of its
 * own either (the old `--abBg` card look is gone) — content shows through to `CustomerLayout`'s
 * ambient `var(--dark060)`, same as every other customer-stack interface.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Tab = "groups" | "users";

const TABS: { key: Tab; label: string }[] = [
  { key: "groups", label: "Teams" },
  { key: "users", label: "Users" },
];

const TAB_KEY = "ab-vault-ca-tab";

interface EntityChangeset {
  entityType: "groups" | "users";
  entityLabel: string;
  toAdd: EntityItem[];
  toRemove: EntityItem[];
}

type PendingChanges = Record<string, EntityChangeset>;
type LoadedAssignments = Record<string, EntityItem[]>;

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

const eKey = (type: "groups" | "users", id: string) => `${type}:${id}`;

const uniqueById = <T extends { id: string }>(arr: T[]): T[] => {
  const seen = new Set<string>();
  return arr.filter((x) => (seen.has(x.id) ? false : (seen.add(x.id), true)));
};

function getWorkingCandidates(
  key: string,
  loaded: LoadedAssignments,
  pending: PendingChanges
): EntityItem[] {
  const base = loaded[key] ?? [];
  const cs = pending[key];
  const merged = cs
    ? (() => {
        const removeIds = new Set(cs.toRemove.map((c) => c.id));
        return [...base.filter((c) => !removeIds.has(c.id)), ...cs.toAdd];
      })()
    : base;
  return uniqueById(merged).sort(sortByKey<EntityItem>("name"));
}

function totalChanges(pending: PendingChanges): number {
  return Object.values(pending).reduce(
    (n, cs) => n + cs.toAdd.length + cs.toRemove.length,
    0
  );
}

// ---------------------------------------------------------------------------
// Screen shell (LOCAL replacement for vault's FeatureLayout — see file header note)
// ---------------------------------------------------------------------------

/**
 * `CustomerLayout`'s `<main>` (`Content`) is already a `flex: 1 1 auto; min-height: 0` column
 * bounded by `Shell`'s definite `height: 100vh` (see that file's doc comment) — `ScreenRoot` opts
 * THIS route into that existing bound (same `flex: 1 1 auto` + `min-height: 0` pairing, one level
 * down) instead of re-deriving the viewport height itself. `AbHeader` keeps its natural (content)
 * height as the first flex child; `ScreenContent` below is the one that grows/shrinks to fill
 * whatever's left, same idea one level down — and so on down through every Table/TreeView on this
 * screen, all one continuous flex-fill chain, no JS-measured height anywhere in it.
 */
const ScreenRoot = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  color: #fff;
`;

/** Fills the full width under `AbHeader` — no page-level max-width, matching NFL's
 *  `PageWrapperTwoColumn` sizing (drag-resizable, ratio persisted to `localStorage`, no cap on how
 *  wide either pane can grow). Also the next link in `ScreenRoot`'s fill chain — grows to consume
 *  whatever height is left under `AbHeader`. */
const ScreenContent = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 20px 22px 40px;
  box-sizing: border-box;
`;

const ScreenCenter = styled.div`
  flex: 1 1 auto;
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface ScreenShellProps {
  loading?: boolean;
  /** Rendered centered in place of `loading`/`children` — `useInterfaceAccess`'s "denied" state
   *  (see the file doc comment's entitlement-gate note below). */
  notice?: React.ReactNode;
  children: React.ReactNode;
}

/** No branding here anymore — `AbHeader` owns the logo/wordmark/tabs (see file doc comment). Just
 *  the sticky header plus the loading-state centering wrapper around real content. */
const ScreenShell = ({ loading, notice, children }: ScreenShellProps) => (
  <ScreenRoot>
    <AbHeader />
    <ScreenContent>
      {notice ? (
        <ScreenCenter>{notice}</ScreenCenter>
      ) : loading ? (
        <ScreenCenter>
          <Spinner />
        </ScreenCenter>
      ) : (
        children
      )}
    </ScreenContent>
  </ScreenRoot>
);

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

/** No max-width — the actual column split (when `admin` shows the `ChangesPanel`) is
 *  `PageWrapperTwoColumn` (drag-resizable, ratio persisted to `localStorage`), same as
 *  `spotter`'s `FieldsScreen`, and it's rendered directly under
 *  `ScreenContent` with nothing capping its width. Non-admin users see only `MainColumn`, no
 *  second pane, so there's nothing to wrap/resize in that case. */
const MainColumn = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
`;

const TabContent = styled.div`
  padding: 15px 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const NewBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 5px;
  background-color: var(--green100);
  color: #fff;
  border-radius: 3px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  flex-shrink: 0;
`;

const RemoveBadge = styled(NewBadge)`
  background-color: var(--red100);
`;

const BannerWrap = styled.div`
  margin-left: auto;
`;

const StyledSelect = styled.div`
  overflow: visible;
  flex: 1 1 auto;
  min-width: 0;
`;

/** Ported from `apps/vault-frontend`'s own `AddRow` — the flex row hosting the "Add a
 *  candidate…" select alongside the "Add all"/"Remove all" bulk buttons (see the
 *  `unassignedCandidates`/`handleAddAll`/`handleRemoveAll` derived state below). */
const AddRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 10px;
`;

/** Local portal host for every `Select` on this screen — react-select's open menu escapes the
 *  page's own layout via a portal, and the app-wide `#PortalTarget` div is a SIBLING of the routed
 *  page content (both children of the app root), outside `VaultThemeProvider`'s `VaultBlueScope`
 *  wrapper. Portaling there means the re-hued `--blue*` vars never reach the open menu. Portaling
 *  into a host rendered as a DOM descendant of this screen instead keeps it inside that scope —
 *  same fix `spotter`'s `FieldsScreen` already applies for its own portaled
 *  Selects (`SelectPortalHost`). `display: contents` keeps it out of layout entirely; react-select
 *  positions the portaled menu with `menuPosition="fixed"` regardless of where this host sits. */
const SelectPortalHost = styled.div`
  display: contents;
`;

const ROW_COLS = [{ width: "1fr" }, { width: "28px" }];
const OWN_ROW_COLS = [{ width: "1fr" }];
const TREE_COLS = [{ width: "1fr" }, { width: "28px" }];

const FilterInput = styled(Input)`
  flex: 0 1 300px;
`;

/**
 * `InputGroup` has no height/flex-fill API of its own (unlike `Section`, whose own `Padding`
 * already documents itself as "allow scrolls to fill height" — see `FillSection` below), so this
 * reaches into its fixed two-child DOM shape (`Title` then `Body`, in that order, whenever a
 * `title`/`buttons` prop is passed, which every usage here does) to opt it into the same fill
 * chain: the outer box becomes the flex item that grows, `Body` (its last child, holding
 * whichever `Table` is nested inside) becomes the flex column that hands that space down to it.
 * `Table`'s own root already defaults to `flex: 1 1 auto`, so it just fills `Body` once `Body`
 * itself has somewhere to grow into — nothing here sizes the table directly. */
const AssignedCandidatesGroup = styled(InputGroup)`
  margin-left: 10px;
  margin-right: 10px;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;

  & > div:first-child {
    flex-shrink: 0;
  }

  & > div:last-child {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
`;

const ROW_HEIGHT = 40;

/** `Section`'s own body wrapper (`Padding`, internal to `@cinesys/ui`) is already `flex-grow: 1;
 *  min-height: 0` — built to fill whatever height `Section` itself is given. Both Sections on this
 *  screen ("Assignments" and "Changes") need to opt INTO that instead of their default
 *  `height: fit-content`, so both panes stretch to the same bottom edge inside
 *  `PageWrapperTwoColumn`'s row (see `FullHeightPageWrapper` below) rather than each sizing to its
 *  own content. */
const FillSection = styled(Section)`
  flex: 1 1 auto;
  min-height: 0;
`;

/**
 * `PageWrapperTwoColumn`'s own two panes already stretch to equal height (`align-items: stretch`
 * on its row, once it has a definite height to stretch INTO — the `flex: 1 1 auto` this adds).
 * Its panes themselves aren't flex containers by default though, so a plain percentage/flex-fill
 * child of either one wouldn't have anywhere to grow — the `:first-child`/`:last-child` rule below
 * (the two panes; the resize handle sits between them and is untouched) turns each into a flex
 * column so `MainColumn`/`FillSection` can hand height down to their own table via the same chain.
 */
const FullHeightPageWrapper = styled(PageWrapperTwoColumn)`
  flex: 1 1 auto;
  min-height: 0;

  & > div:first-child,
  & > div:last-child {
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
`;

/**
 * `Banner`'s own CSS is `margin: auto`, which — now that this screen's panels are full-height
 * flex columns (see the fill chain above) rather than shrink-to-fit — centers a placeholder/error
 * Banner dead in the middle of the panel instead of sitting near the top like an empty-state
 * message normally would. Pins the vertical margin instead, keeping the existing horizontal
 * centering. Used for every Banner rendered as a panel's own content (not inline alongside other
 * controls) so all of them — team/user not-selected, empty candidate list, no pending changes,
 * load errors — land in the same spot regardless of how much empty space is below them.
 */
const TopBanner = styled(Banner)`
  margin-top: 20px;
  margin-bottom: auto;
`;

// ---------------------------------------------------------------------------
// Changes panel (TreeView, Teams → Users)
// ---------------------------------------------------------------------------

interface ChangesPanelProps {
  pending: PendingChanges;
  allCandidates: EntityItem[];
  isSaving: boolean;
  admin: boolean;
  saveError?: string | null;
  onSave: () => void;
  onRevert: () => void;
  onUndo: (key: string, action: "add" | "remove", candidateId: string) => void;
  onNavigateToEntity: (
    entityType: "groups" | "users",
    entityId: string
  ) => void;
}

const ChangesPanel = ({
  pending,
  allCandidates,
  isSaving,
  admin,
  saveError,
  onSave,
  onRevert,
  onUndo,
  onNavigateToEntity,
}: ChangesPanelProps) => {
  const count = totalChanges(pending);
  const candidateMap = new Map(allCandidates.map((c) => [c.id, c]));

  const entries = Object.entries(pending).filter(
    ([, cs]) => cs.toAdd.length > 0 || cs.toRemove.length > 0
  );
  const teamEntries = entries.filter(([, cs]) => cs.entityType === "groups");
  const userEntries = entries.filter(([, cs]) => cs.entityType === "users");

  const treeRows: TreeRow[] = [];

  const buildEntityChildren = (key: string, cs: EntityChangeset) => {
    cs.toAdd.forEach((c) => {
      const name = candidateMap.get(c.id)?.name ?? c.id;
      treeRows.push({
        id: `add:${key}:${c.id}`,
        depth: 1,
        parentId: `entity:${key}`,
        firstElement: (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              minWidth: 0,
              flexWrap: "wrap",
            }}
          >
            <span style={{ color: "var(--green300)", wordBreak: "break-word" }}>
              {name}
            </span>
            <NewBadge>add</NewBadge>
          </span>
        ),
        otherElements: admin ? (
          <Button
            icon={X}
            borderless
            type="danger"
            size={30}
            onClick={(e) => {
              e.stopPropagation();
              onUndo(key, "add", c.id);
            }}
            tooltip="Remove"
          />
        ) : undefined,
      });
    });
    cs.toRemove.forEach((c) => {
      const name = candidateMap.get(c.id)?.name ?? c.id;
      treeRows.push({
        id: `rm:${key}:${c.id}`,
        depth: 1,
        parentId: `entity:${key}`,
        firstElement: (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              minWidth: 0,
              flexWrap: "wrap",
            }}
          >
            <span style={{ color: "var(--red300)", wordBreak: "break-word" }}>
              {name}
            </span>
            <RemoveBadge>remove</RemoveBadge>
          </span>
        ),
        otherElements: admin ? (
          <Button
            icon={X}
            borderless
            type="danger"
            size={30}
            onClick={(e) => {
              e.stopPropagation();
              onUndo(key, "remove", c.id);
            }}
            tooltip="Remove"
          />
        ) : undefined,
      });
    });
  };

  [...teamEntries, ...userEntries].forEach(([key, cs]) => {
    const [type, id] = key.split(":");
    treeRows.push({
      id: `entity:${key}`,
      depth: 0,
      hasChildren: true,
      onClick: () => onNavigateToEntity(type as "groups" | "users", id),
      firstElement: <span>{cs.entityLabel}</span>,
    });
    buildEntityChildren(key, cs);
  });

  return (
    <FillSection
      title="Changes"
      buttons={
        count > 0 && (
          <BannerWrap>
            <ChangesTrigger $tone="pending">
              {count} unsaved change{count !== 1 ? "s" : ""}
            </ChangesTrigger>
          </BannerWrap>
        )
      }
      noMargin
      footer={
        admin && count > 0 ? (
          <>
            <Button
              label="Revert"
              icon={RotateCcw}
              type="danger"
              onClick={onRevert}
              disabled={isSaving}
            />
            <Button
              label="Save"
              icon={Save}
              type="success"
              onClick={onSave}
              loading={isSaving}
            />
          </>
        ) : undefined
      }
    >
      {saveError && <TopBanner error={saveError} />}
      {count === 0 ? (
        <TopBanner placeholder="No pending changes" />
      ) : (
        <TreeView
          columns={TREE_COLS}
          rows={treeRows}
          rowHeight={35}
          buttonSize={30}
          rowPadding="2.5px 10px"
          virtual
        />
      )}
    </FillSection>
  );
};

// ---------------------------------------------------------------------------
// Main screen
// ---------------------------------------------------------------------------

const CandidateAssignmentsScreen = () => {
  const access = useInterfaceAccess(AB_VAULT_INTERFACE_ID);
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<Tab>(() => {
    const saved = localStorage.getItem(TAB_KEY);
    return saved === "groups" || saved === "users" ? saved : "groups";
  });

  const handleTabChange = (value: string) => {
    const t = value as Tab;
    setTab(t);
    localStorage.setItem(TAB_KEY, t);
  };

  const [selectedTeam, setSelectedTeam] = useState<EntityItem | null>(null);
  const [selectedUser, setSelectedUser] = useState<EntityItem | null>(null);
  const [addSelectKey, setAddSelectKey] = useState(0);
  const [selectPortal, setSelectPortal] = useState<HTMLDivElement | null>(null);

  const [allTeams, setAllTeams] = useState<EntityItem[]>([]);
  const [allUsers, setAllUsers] = useState<EntityItem[]>([]);
  const [allCandidates, setAllCandidates] = useState<EntityItem[]>([]);

  const [loadedAssignments, setLoadedAssignments] = useState<LoadedAssignments>(
    {}
  );
  const [pendingChanges, setPendingChanges] = useState<PendingChanges>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [candidateFilter, setCandidateFilter] = useState("");

  // --- Queries ---

  const {
    data: user,
    isLoading: userInfoLoading,
    isError: userInfoError,
  } = useQuery(["ab-vault-user-info"], getUserInfo);
  const rights: VaultRights | null = user?.rights ?? null;
  const notUser = isNotUser(rights);
  const admin = isAdmin(rights);

  const { isLoading: ownLoading, isError: ownError } = useQuery(
    ["ab-vault-own-assignments"],
    getOwnAssignments,
    {
      enabled: rights === "user",
      onSuccess: (own) =>
        setLoadedAssignments((prev) => ({ ...prev, __own__: own })),
    }
  );

  const { isLoading: teamsLoading, isError: teamsError } = useQuery(
    ["ab-vault-teams"],
    getTeams,
    { enabled: notUser, onSuccess: setAllTeams }
  );

  const { isLoading: usersLoading, isError: usersError } = useQuery(
    ["ab-vault-users"],
    getUsers,
    {
      enabled: notUser,
      onSuccess: (users) => setAllUsers(users.filter((u) => u.name?.trim())),
    }
  );

  const { isLoading: candidatesLoading, isError: candidatesError } = useQuery(
    ["ab-vault-candidates"],
    getCandidates,
    { enabled: user !== undefined, onSuccess: setAllCandidates }
  );

  const { isLoading: teamAssignmentsLoading, isError: teamAssignmentsError } =
    useQuery(
      ["ab-vault-entity-assignments", "groups", selectedTeam?.id ?? null],
      () => getAssignmentForEntity("groups", selectedTeam!.id),
      {
        enabled: selectedTeam !== null,
        onSuccess: (assignments) => {
          if (!selectedTeam) {
            return;
          }
          setLoadedAssignments((prev) => ({
            ...prev,
            [eKey("groups", selectedTeam.id)]: assignments,
          }));
        },
      }
    );

  const { isLoading: userAssignmentsLoading, isError: userAssignmentsError } =
    useQuery(
      ["ab-vault-entity-assignments", "users", selectedUser?.id ?? null],
      () => getAssignmentForEntity("users", selectedUser!.id),
      {
        enabled: selectedUser !== null,
        onSuccess: (assignments) => {
          if (!selectedUser) {
            return;
          }
          setLoadedAssignments((prev) => ({
            ...prev,
            [eKey("users", selectedUser.id)]: assignments,
          }));
        },
      }
    );

  // --- Pending change operations ---

  const addCandidate = useCallback(
    (
      entityType: "groups" | "users",
      entityId: string,
      entityLabel: string,
      candidate: EntityItem
    ) => {
      const key = eKey(entityType, entityId);
      setPendingChanges((prev) => {
        const cs = prev[key] ?? {
          entityType,
          entityLabel,
          toAdd: [],
          toRemove: [],
        };
        if (cs.toRemove.some((c) => c.id === candidate.id)) {
          return {
            ...prev,
            [key]: {
              ...cs,
              toRemove: cs.toRemove.filter((c) => c.id !== candidate.id),
            },
          };
        }
        if (cs.toAdd.some((c) => c.id === candidate.id)) {
          return prev;
        }
        return { ...prev, [key]: { ...cs, toAdd: [...cs.toAdd, candidate] } };
      });
    },
    []
  );

  const removeCandidate = useCallback(
    (
      entityType: "groups" | "users",
      entityId: string,
      entityLabel: string,
      candidate: EntityItem
    ) => {
      const key = eKey(entityType, entityId);
      setPendingChanges((prev) => {
        const cs = prev[key] ?? {
          entityType,
          entityLabel,
          toAdd: [],
          toRemove: [],
        };
        if (cs.toAdd.some((c) => c.id === candidate.id)) {
          return {
            ...prev,
            [key]: {
              ...cs,
              toAdd: cs.toAdd.filter((c) => c.id !== candidate.id),
            },
          };
        }
        if (cs.toRemove.some((c) => c.id === candidate.id)) {
          return prev;
        }
        return {
          ...prev,
          [key]: { ...cs, toRemove: [...cs.toRemove, candidate] },
        };
      });
    },
    []
  );

  const undoChange = useCallback(
    (key: string, action: "add" | "remove", candidateId: string) => {
      setPendingChanges((prev) => {
        const cs = prev[key];
        if (!cs) {
          return prev;
        }
        return {
          ...prev,
          [key]: {
            ...cs,
            toAdd:
              action === "add"
                ? cs.toAdd.filter((c) => c.id !== candidateId)
                : cs.toAdd,
            toRemove:
              action === "remove"
                ? cs.toRemove.filter((c) => c.id !== candidateId)
                : cs.toRemove,
          },
        };
      });
    },
    []
  );

  // --- Navigation ---

  const switchTo = useCallback((newTab: Tab, entity: EntityItem) => {
    if (newTab === "groups") {
      setSelectedTeam(entity);
    } else {
      setSelectedUser(entity);
    }
    setTab(newTab);
    localStorage.setItem(TAB_KEY, newTab);
  }, []);

  const navigateToEntity = useCallback(
    (entityType: "groups" | "users", entityId: string) => {
      const entity =
        entityType === "groups"
          ? allTeams.find((t) => t.id === entityId)
          : allUsers.find((u) => u.id === entityId);
      if (entity) {
        switchTo(entityType, entity);
      }
    },
    [allTeams, allUsers, switchTo]
  );

  // --- Save / revert ---

  const handleSave = useCallback(async () => {
    const entries = Object.entries(pendingChanges).filter(
      ([, cs]) => cs.toAdd.length > 0 || cs.toRemove.length > 0
    );
    if (!entries.length) {
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      for (const [key, cs] of entries) {
        const [type, id] = key.split(":");
        const entity: SelectedEntity = {
          type: type as "groups" | "users",
          value: { id, name: cs.entityLabel },
        };
        await saveAssignment(entity, {
          toAdd: cs.toAdd,
          toRemove: cs.toRemove,
        });
        // Apply the changeset to the cache directly — avoids a refetch flash.
        // Compute working candidates before clearing pending so the diff is still visible.
        const saved = getWorkingCandidates(
          key,
          loadedAssignments,
          pendingChanges
        );
        queryClient.setQueryData(
          ["ab-vault-entity-assignments", type, id],
          saved
        );
        setLoadedAssignments((prev) => ({ ...prev, [key]: saved }));
        setPendingChanges((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }
    } catch (err) {
      setSaveError((err as Error).message || "Server Error");
    } finally {
      setIsSaving(false);
    }
  }, [pendingChanges, queryClient]);

  const handleRevert = useCallback(() => setPendingChanges({}), []);

  // --- Loading ---

  // Org-level entitlement gate — see `apps/customer-frontend/src/features/spotter/fields/
  // FieldsScreen.tsx`'s equivalent doc comment. Checked before this screen's own query loading
  // below; "pending"/"denied" both keep `AbHeader`'s placeholder chrome via `ScreenShell`.
  // "pending" renders `DelayedSpinner` through the `notice` slot rather than `loading` (below,
  // used for this screen's own post-grant data loading) — unified with Spotter's equivalent gate,
  // see `DelayedSpinner`'s own doc comment for why a plain immediate `Spinner` isn't used here.
  if (access.status !== "granted") {
    return (
      <ScreenShell
        notice={
          access.status === "denied" ? (
            <InterfaceAccessNotice reason={access.reason} />
          ) : (
            <DelayedSpinner />
          )
        }
      >
        {null}
      </ScreenShell>
    );
  }

  const loading =
    userInfoLoading ||
    (notUser ? teamsLoading || usersLoading : false) ||
    candidatesLoading;

  if (loading) {
    return <ScreenShell loading>{null}</ScreenShell>;
  }

  // --- Derived state ---

  const teamOptions: SelectOption[] = allTeams.map((t) => ({
    value: t.id,
    label: t.name,
  }));
  const userOptions: SelectOption[] = allUsers.map((u) => ({
    value: u.id,
    label: u.name,
  }));

  const activeEntity = tab === "groups" ? selectedTeam : selectedUser;
  const activeEntityType = tab;
  const activeEntityKey = activeEntity
    ? eKey(activeEntityType, activeEntity.id)
    : null;

  const workingCandidates = activeEntityKey
    ? getWorkingCandidates(activeEntityKey, loadedAssignments, pendingChanges)
    : [];

  const assignedCandidateIds = new Set(workingCandidates.map((c) => c.id));
  const availableCandidateOptions: SelectOption[] = allCandidates
    .filter((c) => !assignedCandidateIds.has(c.id))
    .map((c) => ({ value: c.id, label: c.name }));

  const unassignedCandidates = allCandidates.filter(
    (c) => !assignedCandidateIds.has(c.id)
  );

  const entityAssignmentsLoading =
    tab === "groups" ? teamAssignmentsLoading : userAssignmentsLoading;

  // --- Bulk handlers ---
  // Ported from apps/vault-frontend's CandidateAssignmentsScreen.tsx (handleAddAll/
  // handleRemoveAll) — dropped when this screen was originally forked (M1 port predated
  // vault-frontend's PR #183, which added these), then never backported on a later staging
  // merge. Restored here to close that behavioral gap with the old stack.

  const handleAddAll = () => {
    if (!activeEntity) {
      return;
    }
    for (const candidate of unassignedCandidates) {
      addCandidate(
        activeEntityType,
        activeEntity.id,
        activeEntity.name,
        candidate
      );
    }
  };

  const handleRemoveAll = () => {
    if (!activeEntity || workingCandidates.length === 0) {
      return;
    }
    for (const candidate of workingCandidates) {
      removeCandidate(
        activeEntityType,
        activeEntity.id,
        activeEntity.name,
        candidate
      );
    }
  };

  // --- Fuse filter ---

  const fuseFilter = (list: EntityItem[]) => {
    if (!candidateFilter.trim()) {
      return list;
    }
    return new Fuse(list, { keys: ["name"], threshold: 0.4 })
      .search(candidateFilter)
      .map((r) => r.item);
  };

  // --- Row builder ---

  const addRow =
    admin && activeEntity
      ? [
          {
            id: `__add__:${activeEntityKey}`,
            element: () => (
              <AddRow style={{ gridColumn: "1 / -1" }}>
                <StyledSelect>
                  <Select
                    key={addSelectKey}
                    autoFocus={addSelectKey > 0}
                    options={availableCandidateOptions}
                    isSearchable
                    placeholder="Add a candidate…"
                    value={null}
                    onChange={(opt) => {
                      const o = opt as SelectOption;
                      if (!o.value || !activeEntity) {
                        return;
                      }
                      const candidate = allCandidates.find(
                        (c) => c.id === o.value
                      );
                      if (candidate) {
                        addCandidate(
                          activeEntityType,
                          activeEntity.id,
                          activeEntity.name,
                          candidate
                        );
                        setAddSelectKey((k) => k + 1);
                      }
                    }}
                    onClear={() => {}}
                    portalTarget={selectPortal}
                    noPadding
                  />
                </StyledSelect>
                <Button
                  label="Add all"
                  type="success"
                  onClick={handleAddAll}
                  disabled={
                    entityAssignmentsLoading ||
                    unassignedCandidates.length === 0 ||
                    isSaving
                  }
                  tooltip={
                    unassignedCandidates.length === 0
                      ? "All candidates already assigned"
                      : undefined
                  }
                />
                <Button
                  label="Remove all"
                  type="danger"
                  onClick={handleRemoveAll}
                  disabled={
                    entityAssignmentsLoading ||
                    workingCandidates.length === 0 ||
                    isSaving
                  }
                  tooltip={
                    workingCandidates.length === 0
                      ? "No candidates assigned"
                      : undefined
                  }
                />
              </AddRow>
            ),
          },
        ]
      : [];

  const buildCandidateRows = (
    candidates: EntityItem[],
    entityType: "groups" | "users",
    entity: EntityItem
  ) => {
    const key = eKey(entityType, entity.id);
    return uniqueById(candidates).map((candidate) => {
      const isPendingAdd =
        pendingChanges[key]?.toAdd.some((c) => c.id === candidate.id) ?? false;
      return {
        id: candidate.id,
        element: () => (
          <>
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                minWidth: 0,
              }}
            >
              <span
                style={{
                  flex: "0 1 auto",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: isPendingAdd ? "var(--green300)" : undefined,
                }}
              >
                {candidate.name}
              </span>
              {isPendingAdd && <NewBadge>new</NewBadge>}
            </span>
            {admin && (
              <Button
                icon={X}
                type="danger"
                size={30}
                borderless
                onClick={() =>
                  removeCandidate(entityType, entity.id, entity.name, candidate)
                }
                tooltip="Remove"
              />
            )}
          </>
        ),
      };
    });
  };

  // --- Render ---

  const mainColumn = (
    <MainColumn>
      {userInfoError && <TopBanner error="Failed to load user info" />}
      {candidatesError && <TopBanner error="Failed to load candidates" />}
      <FillSection title="Assignments" noMargin>
        {rights === "user" ? (
          // User tier: show the caller's own assignments directly, no entity picker.
          <AssignedCandidatesGroup
            title="Assigned Candidates"
            noHover
            buttons={
              <FilterInput
                icon={Search}
                value={candidateFilter}
                onChange={setCandidateFilter}
                placeholder="Filter candidates…"
                noPadding
                clearable
                maxWidth={300}
                minWidth={180}
                height={35}
              />
            }
          >
            {ownError ? (
              <TopBanner error="Failed to load assignments" />
            ) : ownLoading ? (
              <Spinner />
            ) : (
              (() => {
                const ownCandidates = loadedAssignments["__own__"] ?? [];
                if (ownCandidates.length === 0) {
                  return (
                    <TopBanner placeholder="You have no candidates assigned" />
                  );
                }
                const filtered = fuseFilter(ownCandidates);
                return filtered.length === 0 ? (
                  <TopBanner placeholder="No matching candidates" />
                ) : (
                  <Table
                    columns={OWN_ROW_COLS}
                    columnGrid
                    rowHeight={ROW_HEIGHT}
                    virtual
                    rows={filtered.map((c) => ({
                      id: c.id,
                      element: () => (
                        <span
                          style={{
                            flex: "0 1 auto",
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {c.name}
                        </span>
                      ),
                    }))}
                  />
                );
              })()
            )}
          </AssignedCandidatesGroup>
        ) : (
          <>
            {(teamsError || usersError) && (
              <TopBanner
                error={
                  teamsError && usersError
                    ? "Failed to load teams and users"
                    : teamsError
                    ? "Failed to load teams"
                    : "Failed to load users"
                }
              />
            )}
            <Tabs
              mode="wrap"
              tabs={TABS}
              value={tab}
              onChange={handleTabChange}
            />
            <TabContent>
              {tab === "groups" ? (
                <Select
                  label="Team"
                  options={teamOptions}
                  isSearchable
                  placeholder="Select a team…"
                  value={
                    selectedTeam
                      ? { value: selectedTeam.id, label: selectedTeam.name }
                      : null
                  }
                  onChange={(opt) => {
                    const o = opt as SelectOption;
                    setCandidateFilter("");
                    setSelectedTeam(
                      o.value
                        ? allTeams.find((t) => t.id === o.value) ?? null
                        : null
                    );
                  }}
                  onClear={() => {
                    setCandidateFilter("");
                    setSelectedTeam(null);
                  }}
                  portalTarget={selectPortal}
                  noPadding
                />
              ) : (
                <Select
                  label="User"
                  options={userOptions}
                  isSearchable
                  placeholder="Select a user…"
                  value={
                    selectedUser
                      ? { value: selectedUser.id, label: selectedUser.name }
                      : null
                  }
                  onChange={(opt) => {
                    const o = opt as SelectOption;
                    setCandidateFilter("");
                    setSelectedUser(
                      o.value
                        ? allUsers.find((u) => u.id === o.value) ?? null
                        : null
                    );
                  }}
                  onClear={() => {
                    setCandidateFilter("");
                    setSelectedUser(null);
                  }}
                  portalTarget={selectPortal}
                  noPadding
                />
              )}
            </TabContent>

            {!activeEntity && (
              <TopBanner
                placeholder={
                  tab === "groups"
                    ? "Select a team to view assignments"
                    : "Select a user to view assignments"
                }
              />
            )}

            {activeEntity && (
              <AssignedCandidatesGroup
                title="Assigned Candidates"
                noHover
                buttons={
                  <FilterInput
                    icon={Search}
                    value={candidateFilter}
                    onChange={setCandidateFilter}
                    placeholder="Filter candidates…"
                    noPadding
                    clearable
                    height={35}
                  />
                }
              >
                {(() => {
                  const assignmentsLoading =
                    tab === "groups"
                      ? teamAssignmentsLoading
                      : userAssignmentsLoading;
                  if (assignmentsLoading) {
                    return <Spinner />;
                  }
                  if (
                    tab === "groups"
                      ? teamAssignmentsError
                      : userAssignmentsError
                  ) {
                    return <TopBanner error="Failed to load assignments" />;
                  }
                  const filtered = fuseFilter(workingCandidates);
                  const candidateRows = buildCandidateRows(
                    filtered,
                    activeEntityType,
                    activeEntity
                  );
                  const rows = [...addRow, ...candidateRows];
                  return rows.length === 0 ? (
                    <TopBanner
                      placeholder={
                        candidateFilter
                          ? "No matching candidates"
                          : "No candidates assigned"
                      }
                    />
                  ) : (
                    <Table
                      columns={ROW_COLS}
                      columnGrid
                      rowHeight={ROW_HEIGHT}
                      virtual
                      rows={rows}
                    />
                  );
                })()}
              </AssignedCandidatesGroup>
            )}
          </>
        )}
      </FillSection>
    </MainColumn>
  );

  return (
    <ScreenShell>
      <SelectPortalHost ref={setSelectPortal} />
      {admin ? (
        <FullHeightPageWrapper
          minWidth="1030px"
          id="ab-vault-candidate-assignments"
          padding="0"
          defaultRatio={0.67}
        >
          {mainColumn}
          <ChangesPanel
            pending={pendingChanges}
            allCandidates={allCandidates}
            isSaving={isSaving}
            admin={admin}
            saveError={saveError}
            onSave={handleSave}
            onRevert={handleRevert}
            onUndo={undoChange}
            onNavigateToEntity={navigateToEntity}
          />
        </FullHeightPageWrapper>
      ) : (
        mainColumn
      )}
    </ScreenShell>
  );
};

export default CandidateAssignmentsScreen;
